<template>
  <div class="config-backups-page">
    <div class="config-backups-toolbar">
      <CInputGroup class="config-backups-toolbar__group">
        <CFormInput
          v-model.trim="search"
          type="text"
          autocomplete="off"
          placeholder="Поиск по площадкам и файлам резервных копий"
        />
        <CButton color="secondary" :disabled="loading" @click="refreshAll">
          Обновить
        </CButton>
      </CInputGroup>
      <div class="config-backups-toolbar__meta">
        {{ filteredCards.length }} / {{ cards.length }} площадок
      </div>
    </div>

    <div v-if="loading" class="config-backups-grid">
      <div class="config-backups-card config-backups-card_loading" v-for="idx in 6" :key="idx">
        <span class="loader"></span>
      </div>
    </div>

    <div v-else-if="filteredCards.length" class="config-backups-grid">
      <article
        v-for="card in filteredCards"
        :key="card.division.guid || card.division.uuid || card.division.name"
        class="config-backups-card"
        :class="{ 'config-backups-card_empty': !card.backups.length }"
      >
        <div class="config-backups-card__header">
          <div class="config-backups-card__info">
            <h3 class="config-backups-card__title">{{ card.division.name }}</h3>
            <span
              class="config-backups-card__badge"
              :class="card.backups.length ? 'config-backups-card__badge_filled' : 'config-backups-card__badge_empty'"
            >
              {{ card.backups.length ? `${card.backups.length} файл${pluralFiles(card.backups.length)}` : 'Нет копий' }}
            </span>
          </div>
          <button
            class="config-backups-card__dl-btn"
            :disabled="!card.backups.length || card.downloadLoading"
            @click="downloadLatest(card)"
            title="Скачать последний"
          >
            <span v-if="card.downloadLoading" class="loader loader_sm"></span>
            <template v-else>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Последний
            </template>
          </button>
        </div>

        <p v-if="card.error" class="config-backups-card__error">{{ card.error }}</p>

        <ul v-if="card.backups.length" class="config-backups-list">
          <li
            v-for="backup in card.backups"
            :key="backup.fileName"
            class="config-backups-list__item"
          >
            <span class="config-backups-list__name">{{ backup.fileName }}</span>
            <span class="config-backups-list__date">{{ formatBackupLabel(backup) }}</span>
            <button
              class="config-backups-list__dl"
              :disabled="!!card.fileLoading"
              :class="{ 'config-backups-list__dl_loading': card.fileLoading === backup.fileName }"
              @click="downloadFile(card, backup.fileName)"
              title="Скачать"
            >
              <span v-if="card.fileLoading === backup.fileName" class="loader loader_sm"></span>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
          </li>
        </ul>

        <div v-else class="config-backups-card__empty-body">
          Резервных копий нет
        </div>
      </article>
    </div>

    <div v-else class="config-backups-empty">
      Ничего не найдено.
    </div>
  </div>
</template>

<script>
import { CButton, CFormInput, CInputGroup } from '@coreui/bootstrap-vue'
import { ApiSys, getApiUrl, getHeaders } from '@/services/api'
import { useDefaultStore } from '@/store'

export default {
  name: 'ConfigBackupsPage',
  components: {
    CButton,
    CFormInput,
    CInputGroup
  },
  setup() {
    const store = useDefaultStore()
    return {
      store
    }
  },
  data() {
    return {
      loading: true,
      search: '',
      cards: []
    }
  },
  computed: {
    filteredCards() {
      const search = this.search.trim().toLowerCase()
      if (!search) {
        return this.cards
      }

      return this.cards.filter(card => {
        const haystack = [
          card.division.name,
          card.division.address,
          card.division.guid,
          ...(card.backups || []).map(backup => backup.fileName)
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return haystack.includes(search)
      })
    }
  },
  async created() {
    this.store.setPageTitle('Резервные копии конфигов')
    await this.refreshAll()
  },
  methods: {
    async refreshAll() {
      this.loading = true

      try {
        const [divisionsResponse, backupsResponse] = await Promise.all([
          ApiSys.getAdminDivisions(),
          ApiSys.getAllConfigBackupLists()
        ])
        const divisions = this.normalizeDivisionList(divisionsResponse)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru', { numeric: true, sensitivity: 'base' }))
        const backupsByDivision = this.normalizeAggregateBackups(backupsResponse)

        this.cards = divisions.map(division => ({
          division,
          backups: this.getDivisionBackups(division, backupsByDivision),
          downloadLoading: false,
          fileLoading: null,
          error: ''
        }))
      } catch (error) {
        this.cards = []
        this.$toast.error(error.message || 'Не удалось загрузить список площадок')
      } finally {
        this.loading = false
      }
    },
    normalizeDivisionList(response) {
      if (response?.error) {
        throw new Error(response.error.message || 'Не удалось загрузить площадки')
      }

      if (Array.isArray(response)) {
        return response
      }

      if (Array.isArray(response?.data)) {
        return response.data
      }

      return []
    },
    normalizeAggregateBackups(response) {
      if (response?.error) {
        throw new Error(response.error.message || 'Не удалось загрузить резервные копии')
      }

      const divisions = response?.divisions || response?.data?.divisions || []
      if (!Array.isArray(divisions)) {
        return {}
      }

      return divisions.reduce((acc, divisionEntry) => {
        const key = divisionEntry?.divisionName
        if (!key) {
          return acc
        }

        const backups = Array.isArray(divisionEntry.backups)
          ? divisionEntry.backups
              .map(backup => ({
                fileName: backup?.fileName || backup?.filename || backup?.name || backup?.file,
                createdAt: backup?.createdAt || null,
                sizeBytes: backup?.sizeBytes ?? null
              }))
              .filter(backup => backup.fileName)
          : []

        backups.sort((a, b) => {
          const left = new Date(b.createdAt || 0).getTime()
          const right = new Date(a.createdAt || 0).getTime()
          return left - right
        })

        acc[key] = backups
        return acc
      }, {})
    },
    getDivisionBackups(division, backupsByDivision) {
      const candidates = [
        division.name,
        this.sanitizeDivisionName(division.name)
      ].filter(Boolean)

      for (const candidate of candidates) {
        if (backupsByDivision[candidate]) {
          return backupsByDivision[candidate]
        }
      }

      return []
    },
    sanitizeDivisionName(name) {
      return String(name || '').replace(/[<>:"/\\|?*]/g, '_')
    },
    async downloadLatest(card) {
      card.downloadLoading = true
      card.error = ''

      try {
        await this.downloadBackupFile(card.division.name)
      } catch (error) {
        card.error = error.message || 'Не удалось скачать latest.json'
        this.$toast.error(card.error)
      } finally {
        card.downloadLoading = false
      }
    },
    async downloadFile(card, fileName) {
      card.fileLoading = fileName
      card.error = ''

      try {
        await this.downloadBackupFile(card.division.name, fileName)
      } catch (error) {
        card.error = error.message || `Не удалось скачать ${fileName}`
        this.$toast.error(card.error)
      } finally {
        card.fileLoading = null
      }
    },
    async downloadBackupFile(divisionName, fileName = null) {
      const basePath = `api/v3/config/backup/${encodeURIComponent(divisionName)}`
      const path = fileName
        ? `${basePath}/get/${encodeURIComponent(fileName)}`
        : `${basePath}/latest`

      const response = await fetch(getApiUrl(path), {
        headers: getHeaders()
      })

      if (!response.ok) {
        let message = 'Не удалось скачать резервную копию'

        try {
          const payload = await response.json()
          message = payload?.error?.message || payload?.message || message
        } catch (e) {
          const fallbackText = await response.text()
          if (fallbackText) {
            message = fallbackText
          }
        }

        throw new Error(message)
      }

      const blob = await response.blob()
      const downloadName = `config.json`
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    formatBackupLabel(backup) {
      if (backup?.createdAt) {
        const date = new Date(backup.createdAt)
        const formattedDate = Number.isNaN(date.getTime())
          ? null
          : date.toLocaleString('ru-RU')
        const formattedSize = typeof backup.sizeBytes === 'number'
          ? `${this.formatBytes(backup.sizeBytes)}`
          : null

        return [formattedDate, formattedSize].filter(Boolean).join(' | ')
      }

      const fileName = typeof backup === 'string' ? backup : backup?.fileName
      const match = /config_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})\.json/i.exec(fileName || '')
      if (!match) {
        return fileName || ''
      }

      const [, year, month, day, hours, minutes, seconds] = match
      return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`
    },
    formatBytes(sizeBytes) {
      if (sizeBytes < 1024) {
        return `${sizeBytes} B`
      }

      if (sizeBytes < 1024 * 1024) {
        return `${(sizeBytes / 1024).toFixed(1)} KB`
      }

      return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
    },
    pluralFiles(n) {
      const mod10 = n % 10
      const mod100 = n % 100
      if (mod10 === 1 && mod100 !== 11) return ''
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'а'
      return 'ов'
    }
  }
}
</script>

<style scoped>
.config-backups-page {
  padding: 1.5rem 2rem;
  height: 100%;
  overflow-y: auto;
}

/* ── Toolbar ───────────────────────────────────────── */
.config-backups-toolbar {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.config-backups-toolbar__group {
  width: min(100%, 640px);
}

.config-backups-toolbar__meta {
  font-size: 0.75rem;
  color: #606c79;
  white-space: nowrap;
}

/* ── Grid ──────────────────────────────────────────── */
.config-backups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
  align-items: start;
}

/* ── Card ──────────────────────────────────────────── */
.config-backups-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid #3d8bfd;
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.config-backups-card_empty {
  border-left-color: rgba(255, 255, 255, 0.15);
  opacity: 0.7;
}

.config-backups-card_loading {
  min-height: 100px;
  align-items: center;
  justify-content: center;
  border-left-color: rgba(255, 255, 255, 0.1);
}

/* ── Card header ───────────────────────────────────── */
.config-backups-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.config-backups-card__info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.config-backups-card__title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
}

.config-backups-card__badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.15em 0.55em;
  border-radius: 20px;
  letter-spacing: 0.01em;
  width: fit-content;
}

.config-backups-card__badge_filled {
  background: rgba(61, 139, 253, 0.15);
  color: #7eb8ff;
}

.config-backups-card__badge_empty {
  background: rgba(255, 255, 255, 0.06);
  color: #606c79;
}

/* "Download latest" button in card header */
.config-backups-card__dl-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  color: #7eb8ff;
  background: rgba(61, 139, 253, 0.12);
  border: 1px solid rgba(61, 139, 253, 0.25);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.config-backups-card__dl-btn:hover:not(:disabled) {
  background: rgba(61, 139, 253, 0.22);
  color: #b0d4ff;
}

.config-backups-card__dl-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Error ─────────────────────────────────────────── */
.config-backups-card__error {
  margin: 0;
  color: #ff7b7b;
  font-size: 0.75rem;
  padding: 0.35rem 0.5rem;
  background: rgba(255, 100, 100, 0.08);
  border-radius: 5px;
}

/* ── Empty card body ───────────────────────────────── */
.config-backups-card__empty-body {
  font-size: 0.78rem;
  color: #4e5a66;
  padding: 0.25rem 0;
}

/* ── File list ─────────────────────────────────────── */
.config-backups-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.config-backups-list__item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.32rem 0.4rem;
  border-radius: 5px;
  transition: background 0.12s;
}

.config-backups-list__item:hover {
  background: rgba(255, 255, 255, 0.04);
}

.config-backups-list__name {
  font-size: 0.75rem;
  font-family: ui-monospace, 'SFMono-Regular', 'Cascadia Code', monospace;
  color: #c7d0d9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.config-backups-list__date {
  font-size: 0.7rem;
  color: #556070;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Per-file download icon button */
.config-backups-list__dl {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: #556070;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: color 0.12s, background 0.12s, border-color 0.12s;
}

.config-backups-list__dl:hover:not(:disabled) {
  color: #7eb8ff;
  background: rgba(61, 139, 253, 0.12);
  border-color: rgba(61, 139, 253, 0.2);
}

.config-backups-list__dl:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ── Empty search result ───────────────────────────── */
.config-backups-empty {
  color: #4e5a66;
  padding: 2rem 0;
  font-size: 0.875rem;
}

/* ── Spinner ───────────────────────────────────────── */
.loader {
  width: 28px;
  height: 28px;
  border: 2.5px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 0.65s linear infinite;
}

.loader_sm {
  width: 13px;
  height: 13px;
  border-width: 2px;
  flex-shrink: 0;
}

@keyframes rotation {
  to { transform: rotate(360deg); }
}

@media (max-width: 767px) {
  .config-backups-page {
    padding: 1rem;
  }

  .config-backups-grid {
    grid-template-columns: 1fr;
  }
}
</style>
